import { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import LayoutPartner from '../../components/BasicComponent/LayoutPartner'
import FixedBottom from '../../components/BasicComponent/FixedBottom'
import BaseButton from '../../components/BasicComponent/BaseButton'
import BasePopup from '../../components/BasicComponent/BasePopup'
import LoadingScreen from '../../components/BasicComponent/LoadingScreen'
import ModalPaymentQR from './components/ModalPaymentQR/ModalPaymentQR'
import AutoTrafficFinePayment from './components/AutoTrafficFinePayment'
import AutomatedTrafficFineNotificationPaymentInfo from './components/AutomatedTrafficFineNotificationPaymentInfo'
import ReferralCodeInput from './components/ReferralCodeInput'
import TermsCheckbox from '../../components/BasicComponent/TermsCheckbox'
import { PATH } from '../../constants/router'
import { number_to_price } from '../../helper/common'
import { useAppParamsContext } from '../../context/AppParamsContext'
import useThemeFeatureConfig from '../../hooks/common/useThemeFeatureConfig'
import useGetDetailOrderPayment from '../../hooks/payment/useGetDetailOrderPayment'
import useTrafficFinePayment from '../../hooks/payment/useTrafficFinePayment'
import useTrafficFineOrderPaymentPolling from '../../hooks/payment/useTrafficFineOrderPaymentPolling'
import { usePartnerStrategy } from '../../hooks/payment/partner'
import './index.scss'

function buildAmountsFromProduct(product, vehicleCount) {
  const safeVehicleCount = Math.max(Number(vehicleCount) || 0, 1)
  const productPrice = Number(product?.productPrice) || 0
  const productPriceAfterVAT = Number(product?.productPriceAfterVAT) || productPrice

  const subTotalAmount = productPrice * safeVehicleCount
  const totalAmount = productPriceAfterVAT * safeVehicleCount
  const taxAmount = Math.max(totalAmount - subTotalAmount, 0)

  return {
    subTotalAmount,
    taxAmount,
    discountAmount: 0,
    totalAmount
  }
}

export default function AutomatedTrafficFineNotificationPayment() {
  const history = useHistory()
  const location = useLocation()
  const locationState = location.state || {}
  const { hidePaymentMethod, isDisplayReferUserCode, partnerName } = useAppParamsContext()
  const { showTermsCheckbox, hidePaymentMethodSelector, buttonSubmitText, headerTitle } = useThemeFeatureConfig('AUTOMATED_TRAFFIC_FINE_NOTIFICATION_PAYMENT')
  const partnerStrategy = usePartnerStrategy()

  const [referralData, setReferralData] = useState({ referUserCode: null, referUserId: null })
  const [isTermsAccepted, setIsTermsAccepted] = useState(false)

  const {
    orderId,
    isLoading: isLoadingOrderDetail,
    orderDetail,
    vehicles: orderVehicles,
    product: orderProduct,
    paymentMethodId: restoredPaymentMethodId,
    paymentMethodType: restoredPaymentMethodType
  } = useGetDetailOrderPayment()
  const shouldRestoreOrderDetail = !!orderId

  // Vehicles & services lay tu state khi vao moi, hoac tu order detail khi co orderId.
  const stateVehicles = useMemo(() => (Array.isArray(locationState.vehicles) ? locationState.vehicles : []), [locationState.vehicles])
  const stateServices = useMemo(
    () => (Array.isArray(locationState.services) ? locationState.services : []),
    [locationState.services]
  )

  const vehicles = shouldRestoreOrderDetail ? orderVehicles : stateVehicles
  const services = useMemo(() => {
    if (shouldRestoreOrderDetail) {
      return orderProduct ? [orderProduct] : []
    }

    return stateServices
  }, [orderProduct, shouldRestoreOrderDetail, stateServices])

  const [paymentMethod, setPaymentMethod] = useState(null)

  const {
    isSubmitting,
    errorMessage,
    setErrorMessage,
    paymentDetail,
    isOpenQrCodePayment,
    setIsOpenQrCodePayment,
    pendingOrderId,
    disabledPayBtn,
    handlePayment
  } = useTrafficFinePayment({
    orderDetail,
    vehicles,
    services,
    paymentMethod,
    paymentMethodId: restoredPaymentMethodId,
    paymentMethodType: restoredPaymentMethodType,
    successPath: PATH.AUTO_TRAFFIC_FINE_NOTIFICATION_SUCCESS,
    referUserIdFromInput: referralData.referUserId ?? undefined,
    referUserCodeFromInput: referralData.referUserCode ?? undefined
  })

  // Khi QR dang hien va da co orderId, polling trang thai 5s/lan;
  // thanh toan thanh cong thi dong QR va dieu huong sang man Success.
  useTrafficFineOrderPaymentPolling({
    orderId: pendingOrderId,
    enabled: isOpenQrCodePayment,
    onSuccess: ({ orderId: paidOrderId }) => {
      setIsOpenQrCodePayment(false)
      history.push(PATH.AUTO_TRAFFIC_FINE_NOTIFICATION_SUCCESS.replace(':id', paidOrderId))
    }
  })

  // Handle redirect callback status (delegated to strategy)
  partnerStrategy.usePaymentPageEffect({ location, setErrorMessage })

  // Vao thang khong co data thi quay ve trang chon goi.
  useEffect(() => {
    if (shouldRestoreOrderDetail) {
      return
    }

    if (vehicles.length === 0 || services.length === 0) {
      history.replace(PATH.TRAFFIC_FINE_SERVICES)
    }
  }, [history, services.length, shouldRestoreOrderDetail, vehicles.length])

  const computedAmounts = useMemo(() => buildAmountsFromProduct(services[0], vehicles.length), [services, vehicles.length])

  const subTotalAmount = orderDetail?.subTotalAmount ?? computedAmounts.subTotalAmount
  const taxAmount = orderDetail?.taxAmount ?? computedAmounts.taxAmount
  const discountAmount = orderDetail?.discountAmount ?? computedAmounts.discountAmount
  const totalAmount = orderDetail?.totalAmount ?? computedAmounts.totalAmount
  const usageDate = services[0]?.usageDate

  const showPaymentMethodSelector = !(hidePaymentMethod || hidePaymentMethodSelector || orderDetail?.orderId)
  const isPayBtnDisabled = disabledPayBtn || (showTermsCheckbox && !isTermsAccepted)

  const isInitialLoading = shouldRestoreOrderDetail && isLoadingOrderDetail && !orderDetail

  if (isInitialLoading) {
    return (
      <LayoutPartner headerTitle={headerTitle}>
        <LoadingScreen open />
      </LayoutPartner>
    )
  }

  return (
    <LayoutPartner headerTitle={headerTitle}>

      <div className="AutomatedTrafficFineNotificationPayment">
        <AutomatedTrafficFineNotificationPaymentInfo
          vehicles={vehicles}
          services={services}
          subTotalAmount={subTotalAmount}
          taxAmount={taxAmount}
          discountAmount={discountAmount}
          usageDate={usageDate}
          totalAmount={totalAmount}
          referUserCode={referralData.referUserCode}
        />

        {isDisplayReferUserCode && !orderDetail?.orderId && (
          <ReferralCodeInput onReferralValidated={setReferralData} />
        )}

        {showTermsCheckbox && (
          <TermsCheckbox 
            checked={isTermsAccepted} 
            onChange={(e) => setIsTermsAccepted(e.target.checked)} 
            partnerName={partnerName}
          />
        )}

        <AutoTrafficFinePayment 
          paymentMethod={paymentMethod} 
          setPaymentMethod={setPaymentMethod} 
          hideUi={!showPaymentMethodSelector}
        />
      </div>

      <FixedBottom elementPaddingBottom="LayoutPartner">
        <BaseButton onClick={handlePayment} disabled={isPayBtnDisabled}>
          {buttonSubmitText}
        </BaseButton>
      </FixedBottom>

      <ModalPaymentQR
        open={isOpenQrCodePayment}
        onClose={() => setIsOpenQrCodePayment(false)}
        driver={{
          totalPay: paymentDetail?.totalPayment ?? paymentDetail?.totalAmount ?? 0,
          formatedTotalPay: number_to_price(paymentDetail?.totalPayment ?? paymentDetail?.totalAmount ?? 0),
          qr: paymentDetail,
          expiredInMinutes: 10,
          runTime: new Date()
        }}
        method={paymentDetail?.paymentMethodId}
      />

      <BasePopup
        open={!!errorMessage}
        onClose={() => setErrorMessage(null)}
        title={errorMessage?.title || 'Đã xảy ra lỗi'}
        content={errorMessage?.content || ''}
        confirmText="Đã hiểu"
      />

      <LoadingScreen open={isSubmitting} />
    </LayoutPartner>
  )
}
